/**
 * @author: lv.chen
 * @date: 2026/3/27
 * @desc: 数据对比气泡组件，用于展示两个时间段的数据对比信息
 */
import React from 'react';
import classnames from 'classnames';
import GameImage from '@home/components/GameImage';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { formatKMB } from '@intelligence/v3/utils/number';
import { calcRatioDateRange } from '@intelligence/v3/utils/topcharts';

import './index.scss';

/**
 * 指标环比对比说明数据
 */
export interface IDataCompareData {
  /** 图标 URL */
  icon?: string;
  /** 标题 */
  title?: string;
  /** 日期粒度 */
  granularity?: IGranularity;
  /** 当前周期的开始日期 */
  startDate?: string;
  /** 当前周期的结束日期 */
  endDate?: string;
  /** 当前周期的指标值 */
  value?: number | string;
  /** 前一周期的指标值 */
  prevValue?: number | string;
}

/**
 * 其他辅助性配置
 */
export interface IDataCompareHelper {
  /** 系统类型：home、intelligence、dashboard等，默认为intelligence */
  moduleType?: 'home' | 'intelligence' | (string & {});
  /** 实体类型：mobile、pc、console、combine、company，默认为mobile */
  entityType?: 'mobile' | 'pc' | 'console' | 'combine' | 'company' | (string & {});
  /** 指标value的formatter方法 */
  valueFormatter?: (value?: number | string) => string;
}

interface IDataCompareBubbleContentProps extends IBaseProps, IDataCompareData, IDataCompareHelper {}

const dateFormatMap: Record<IGranularity, string> = {
  hourly: 'YYYY-MM-DD HH:mm',
  daily: 'YYYY-MM-DD',
  weekly: 'YYYY-MM-DD',
  monthly: 'YYYY-MM',
  quarterly: 'YYYY-QQ',
  yearly: 'YYYY',
};
const stringifyDateRange = (granularity?: IGranularity, startDate?: string, endDate?: string): string => {
  const granularityFormat = dateFormatMap[granularity as unknown as IGranularity] || dateFormatMap.daily;
  const formattedStartDate = formatDateStr(startDate, granularityFormat);
  const formattedEndDate = formatDateStr(endDate, granularityFormat);

  // 单点时间（start === end）不展示范围格式，直接返回单个时间点
  if (formattedStartDate === formattedEndDate) {
    return formattedStartDate;
  }

  return `${formattedStartDate} ~ ${formattedEndDate}`;
};

const DataCompareBubbleContent: React.FC<IDataCompareBubbleContentProps> = (props) => {
  const {
    style,
    className,
    moduleType = 'intelligence',
    entityType = 'mobile',
    icon,
    title,
    granularity,
    startDate,
    endDate,
    value,
    prevValue,
    valueFormatter = formatKMB,
  } = props;

  // 计算前一周期的日期范围
  const ratioDateRange = calcRatioDateRange(granularity, startDate, endDate);
  const prevStartDate = ratioDateRange?.[0] || '';
  const prevEndDate = ratioDateRange?.[1] || '';

  return (
    <div style={style} className={classnames('dbi-component-data-compare-bubble-content', className)}>
      {/* 头部：图标 + 标题 */}
      {(icon || title) && (
        <div className="dbi-component-data-compare-bubble-content__header">
          {icon && (
            <div className={classnames('dbi-component-data-compare-bubble-content__icon', entityType)}>
              <GameImage moduleType={moduleType} entityType={entityType} src={icon} alt={title} />
            </div>
          )}
          {title && <span className="dbi-component-data-compare-bubble-content__title">{title}</span>}
        </div>
      )}

      {/* 主体：数据对比 */}
      <div className="dbi-component-data-compare-bubble-content__body">
        {/* 左侧：时间段 */}
        <div className="dbi-component-data-compare-bubble-content__left">
          <span className="dbi-component-data-compare-bubble-content__period">
            {stringifyDateRange(granularity, startDate, endDate)}
          </span>
          <div className="dbi-component-data-compare-bubble-content__divider" />
          <span className="dbi-component-data-compare-bubble-content__period">
            {stringifyDateRange(granularity, prevStartDate, prevEndDate)}
          </span>
        </div>

        {/* 中间：VS */}
        <div className="dbi-component-data-compare-bubble-content__vs">VS</div>

        {/* 右侧：数值 */}
        <div className="dbi-component-data-compare-bubble-content__right">
          <span className="dbi-component-data-compare-bubble-content__value">{valueFormatter(value)}</span>
          <div className="dbi-component-data-compare-bubble-content__divider" />
          <span className="dbi-component-data-compare-bubble-content__value">{valueFormatter(prevValue)}</span>
        </div>
      </div>
    </div>
  );
};

export default DataCompareBubbleContent;
